import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { TopUpRequest, TopUpRequestStateTypes } from "./TopUpRequest.util";

const name = "top-up-request";
const extraActions = createExtraActions();

const TopUpRequestInitialState: TopUpRequestStateTypes = {
  approveTopUpRequest: ResetApiState(null),
  resetApproveTopUpRequest: ResetApiState(null),
  rejectTopUpRequest: ResetApiState(null),
  resetRejectTopUpRequest: ResetApiState(null),
  editTopUpRequest: ResetApiState(null),
  resetEditTopUpRequest: ResetApiState(null),
  topUpRequests: ResetApiState([]),
  pagedTopUpRequests: ResetApiState(ResetPaginationState()),
  topUpRequest: ResetApiState(null),
  resetTopUpRequest: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const approveTopUpRequest = createAsyncThunk<
    ResponseWrapper<{
      raw: any[];
      affected: number;
    }>,
    any
  >(`/approve-${name}`, async (id: any, thunkApi) =>
    axios
      .put(`${name}/approve/${id}`)
      .then((response) => {
        if (response.data.success) {
          return id;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error?.response?.data);
      })
  );

  const resetApproveTopUpRequest = createAction(`/reset-approve-${name}`);

  const rejectTopUpRequest = createAsyncThunk<
    ResponseWrapper<{
      raw: any[];
      affected: number;
    }>,
    any
  >(`/reject-${name}`, async (id: any, thunkApi) =>
    axios
      .put(`${name}/reject/${id}`)
      .then((response) => {
        if (response.data.success) {
          return id;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error?.response?.data);
      })
  );

  const resetRejectTopUpRequest = createAction(`/reset-reject-${name}`);

  const editTopUpRequest = createAsyncThunk<ResponseWrapper<TopUpRequest>, any>(
    `/revert-${name}`,
    async (id: any, thunkApi) =>
      axios
        .put(`/${name}/revert/${id}`)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditTopUpRequest = createAction(`/reset-edit-${name}`);

  const getTopUpRequests = createAsyncThunk<TopUpRequest[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedTopUpRequests = createAsyncThunk<
    ResponseWrapper<PaginationType<TopUpRequest[]>>,
    any
  >(`/get-paged-${name}s`, async (query: any, thunkApi) => {
    return axios
      .get(`/${name}/paged${queryStringGenerator(query)}`)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });

  const getTopUpRequest = createAsyncThunk<ResponseWrapper<TopUpRequest>, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetTopUpRequest = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    approveTopUpRequest,
    resetApproveTopUpRequest,
    rejectTopUpRequest,
    resetRejectTopUpRequest,
    editTopUpRequest,
    resetEditTopUpRequest,
    getTopUpRequests,
    getPagedTopUpRequests,
    getTopUpRequest,
    resetTopUpRequest,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<TopUpRequestStateTypes>
) {
  return {
    ...approveTopUpRequest(),
    ...resetApproveTopUpRequest(),
    ...rejectTopUpRequest(),
    ...resetRejectTopUpRequest(),
    ...editTopUpRequestReducer(),
    ...resetEditTopUpRequest(),
    ...topUpRequestsReducer(),
    ...pagedTopUpRequestsReducer(),
    ...topUpRequestReducer(),
    ...resetTopUpRequest(),
    ...resetStatesReducer(),
  };

  function approveTopUpRequest() {
    return {
      ...builder.addCase(
        extraActions.approveTopUpRequest.pending,
        (state, action) => {
          state.approveTopUpRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.approveTopUpRequest.fulfilled,
        (state, action) => {
          state.approveTopUpRequest = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.approveTopUpRequest.rejected,
        (state, action) => {
          state.approveTopUpRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetApproveTopUpRequest() {
    return {
      ...builder.addCase(
        extraActions.resetApproveTopUpRequest,
        (state, action) => {
          state.resetApproveTopUpRequest = ResetApiState(true);
          state.approveTopUpRequest = ResetApiState(null);
        }
      ),
    };
  }

  function rejectTopUpRequest() {
    return {
      ...builder.addCase(
        extraActions.rejectTopUpRequest.pending,
        (state, action) => {
          state.rejectTopUpRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.rejectTopUpRequest.fulfilled,
        (state, action) => {
          state.rejectTopUpRequest = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.rejectTopUpRequest.rejected,
        (state, action) => {
          state.rejectTopUpRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetRejectTopUpRequest() {
    return {
      ...builder.addCase(
        extraActions.resetRejectTopUpRequest,
        (state, action) => {
          state.resetRejectTopUpRequest = ResetApiState(true);
          state.rejectTopUpRequest = ResetApiState(null);
        }
      ),
    };
  }

  function editTopUpRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.editTopUpRequest.pending,
        (state, action) => {
          state.editTopUpRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editTopUpRequest.fulfilled,
        (state, action) => {
          state.editTopUpRequest = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editTopUpRequest.rejected,
        (state, action) => {
          state.editTopUpRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditTopUpRequest() {
    return {
      ...builder.addCase(
        extraActions.resetEditTopUpRequest,
        (state, action) => {
          state.resetEditTopUpRequest = ResetApiState(true);
          state.editTopUpRequest = ResetApiState(null);
        }
      ),
    };
  }

  function topUpRequestsReducer() {
    return {
      ...builder.addCase(
        extraActions.getTopUpRequests.pending,
        (state, action) => {
          state.topUpRequests = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTopUpRequests.fulfilled,
        (state, action) => {
          state.topUpRequests = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTopUpRequests.rejected,
        (state, action) => {
          state.topUpRequests = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function pagedTopUpRequestsReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedTopUpRequests.pending,
        (state, action) => {
          state.pagedTopUpRequests = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedTopUpRequests.fulfilled,
        (state, action) => {
          state.pagedTopUpRequests = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedTopUpRequests.rejected,
        (state, action) => {
          state.pagedTopUpRequests = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function topUpRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.getTopUpRequest.pending,
        (state, action) => {
          state.topUpRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTopUpRequest.fulfilled,
        (state, action) => {
          state.topUpRequest = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTopUpRequest.rejected,
        (state, action) => {
          state.topUpRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetTopUpRequest() {
    return {
      ...builder.addCase(extraActions.resetTopUpRequest, (state, action) => {
        state.resetTopUpRequest = ResetApiState(true);
        state.topUpRequest = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.approveTopUpRequest = ResetApiState(null);
        state.resetApproveTopUpRequest = ResetApiState(null);
        state.rejectTopUpRequest = ResetApiState(null);
        state.resetRejectTopUpRequest = ResetApiState(null);
        state.topUpRequests = ResetApiState([]);
        state.topUpRequest = ResetApiState(null);
        state.resetTopUpRequest = ResetApiState(null);
      }),
    };
  }
}

const TopUpRequestSlice = createSlice({
  name,
  initialState: TopUpRequestInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const TopUpRequestActions = {
  ...TopUpRequestSlice.actions,
  ...extraActions,
};
export const TopUpRequestReducer = TopUpRequestSlice.reducer;
